/**
 * Workspaces admin handler
 * Manages workspaces within organizations
 */

import { useState, useCallback, useEffect } from "react";
import type { accountTypes } from "@pineseed/spec";
import { useBaseEntityAdmin, type EntityAdminHandler } from "../useEntityAdmin";
import type { UseAdminAppReturn } from "../useAdminApp";

type Workspace = accountTypes["Workspace"];
type WorkspaceHierarchy = accountTypes["WorkspaceHierarchy"];

export interface WorkspacesAdminHandler extends EntityAdminHandler<Workspace> {
  // Workspace-specific operations
  getWorkspacesByOrganization: (organizationId: string) => Promise<void>;
  getWorkspaceHierarchy: (workspaceId: string) => Promise<WorkspaceHierarchy | null>;
  setAsGlobalWorkspace: (workspaceId: string) => void;
  getStats: () => {
    total: number;
    byOrganization: Record<string, number>;
  };
}

export function useWorkspacesAdmin(adminApp: UseAdminAppReturn): WorkspacesAdminHandler {
  // Base CRUD operations
  const baseHandler = useBaseEntityAdmin<Workspace>({
    adminApp,
    endpoint: 'accounts', // Will be overridden by custom loadEntities
    entityName: 'workspace',
    crudEndpoint: 'workspace',
    workspaceScoped: false,
  });

  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(null);

  // Override loadEntities to load all workspaces
  const loadEntities = useCallback(async (params?: Record<string, string>) => {
    baseHandler.setLoading(true);
    baseHandler.setError(null);
    
    try {
      // Fetch all accounts
      const accountsResponse = await fetch(`${adminApp.httpUrl}/api/accounts`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      if (!accountsResponse.ok) {
        throw new Error('Failed to fetch accounts');
      }
      
      const accounts = await accountsResponse.json();
      const allWorkspaces: Workspace[] = [];
      
      // Fetch organizations and workspaces for each account
      for (const account of accounts) {
        const orgsResponse = await fetch(
          `${adminApp.httpUrl}/api/account/${account.id}/organizations`,
          {
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          }
        );
        
        if (orgsResponse.ok) {
          const orgs = await orgsResponse.json();
          
          // Fetch workspaces for each organization
          for (const org of orgs) {
            const workspacesResponse = await fetch(
              `${adminApp.httpUrl}/api/organization/${org.id}/workspaces`,
              {
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
              }
            );
            
            if (workspacesResponse.ok) {
              const workspaces = await workspacesResponse.json();
              allWorkspaces.push(...workspaces);
            }
          }
        }
      }
      
      baseHandler.setEntities(allWorkspaces);
      console.log(`Loaded ${allWorkspaces.length} workspace(s)`);
    } catch (err) {
      const errorMsg = `Failed to load workspaces: ${err instanceof Error ? err.message : 'Unknown error'}`;
      baseHandler.setError(errorMsg);
      console.error(errorMsg);
      baseHandler.setEntities([]);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, baseHandler]);

  // Get workspaces for a specific organization
  const getWorkspacesByOrganization = useCallback(async (organizationId: string) => {
    baseHandler.setLoading(true);
    baseHandler.setError(null);
    setCurrentOrganizationId(organizationId);
    
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/organization/${organizationId}/workspaces`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [GET /organization/${organizationId}/workspaces]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Failed to fetch workspaces');
      }
      
      const workspaces = await response.json();
      baseHandler.setEntities(workspaces);
      console.log(`✅ Loaded ${workspaces.length} workspace(s) for organization ${organizationId}`);
    } catch (err) {
      const errorMsg = `Failed to load workspaces: ${err instanceof Error ? err.message : 'Unknown error'}`;
      baseHandler.setError(errorMsg);
      console.error(errorMsg);
      baseHandler.setEntities([]);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, baseHandler]);

  // Get workspace hierarchy
  const getWorkspaceHierarchy = useCallback(async (workspaceId: string): Promise<WorkspaceHierarchy | null> => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/workspace/${workspaceId}/hierarchy`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [GET /workspace/${workspaceId}/hierarchy]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        return null;
      }
      
      const hierarchy = await response.json();
      console.log(`✅ Loaded hierarchy for workspace ${workspaceId}`);
      return hierarchy;
    } catch (err) {
      console.error('Failed to get workspace hierarchy:', err);
      return null;
    }
  }, [adminApp]);

  // Set workspace as global workspace
  const setAsGlobalWorkspace = useCallback((workspaceId: string) => {
    adminApp.setWorkspaceId(workspaceId);
    console.log(`✅ Set global workspace to: ${workspaceId}`);
  }, [adminApp]);

  // Get statistics
  const getStats = useCallback(() => {
    const byOrganization = baseHandler.entities.reduce((acc, workspace) => {
      acc[workspace.organizationId] = (acc[workspace.organizationId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: baseHandler.entities.length,
      byOrganization,
    };
  }, [baseHandler.entities]);

  // Load all workspaces on mount
  useEffect(() => {
    loadEntities();
  }, []); // Only on mount

  return {
    ...baseHandler,
    loadEntities,
    getWorkspacesByOrganization,
    getWorkspaceHierarchy,
    setAsGlobalWorkspace,
    getStats,
  };
}

